import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { ArrowPathRoundedSquareIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/20/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { get } from 'lodash'
import { Fragment } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'

import { getCurrentOrganizationId } from '@/components/App/selectors'
import { useRoles } from '@/components/RolesContainer'
import SharedWith from '@/components/Segments/Builder/SharedWith'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import useFeatures from '@/components/useFeatures'
import { BUILDER_ORG_ADMIN_ACCESS } from '@/constants/featureToggles'
import { WorkgridCatalogDatasource, WorkgridDatasource } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { dataSourceV3QueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'
import { getCurrentUser } from '@/store/selectors'
import cn from '@/utilities/cn'

type Props = {
  workgridDataSource: WorkgridCatalogDatasource
  dataSourceInstanceId: string
  readOnly?: boolean
  isDeleting?: boolean
  onEdit?: (dataSource: WorkgridDatasource) => void
  onDelete?: (dataSource: WorkgridDatasource) => void
  onSwapInstance?: (dataSource: WorkgridDatasource) => void
  isTemplate?: boolean
}

export default function DataSourceRow({
  workgridDataSource,
  dataSourceInstanceId,
  isDeleting = false,
  onEdit,
  readOnly,
  onDelete,
  onSwapInstance,
  isTemplate = false
}: Props) {
  const { spaceId } = useParams<{ spaceId: string }>()

  const dataSourcesV3Query = useQuery<WorkgridDatasource>({
    ...dataSourceV3QueryOptions({
      spaceId,
      datasourceId: workgridDataSource?.id,
      instanceId: dataSourceInstanceId
    }),
    enabled: !!workgridDataSource && !isTemplate
  })
  const dataSourceV3 = dataSourcesV3Query.data

  const isAuthenticated =
    isTemplate ||
    dataSourceV3?.status === 'connected' ||
    dataSourcesV3Query.isLoading ||
    dataSourcesV3Query.isIdle ||
    get(dataSourcesV3Query, 'error.status') === 403

  const owners = dataSourceV3?.owners

  const currentUser = useAppSelector(getCurrentUser)

  const isDataSourceOwner = owners?.includes(currentUser?.id)

  const organizationId = useAppSelector(getCurrentOrganizationId)
  const roles = useRoles()
  const hasOrgAdminRole = roles.hasOrganizationAdminRole(organizationId)
  const features = useFeatures()
  const datasourceOrgAdminAccessFlagOn = !!features[BUILDER_ORG_ADMIN_ACCESS]
  return (
    <li
      data-testid="data-source-row"
      className={cn(
        isAuthenticated ? 'hover:bg-zinc-50' : 'bg-red-50 hover:bg-red-100',
        { 'pointer-events-none bg-zinc-50': isDeleting },
        'relative w-full',
        'fade-in flex items-center justify-between hover:cursor-pointer'
      )}
    >
      <Menu as="div" data-testid="edit-menu" className="flex w-full">
        <MenuButton
          className={cn('flex w-full min-w-0 items-center justify-center gap-x-4 py-4 pl-2 hover:cursor-pointer')}
        >
          <img className="mr-2 h-8 w-8 flex-none rounded bg-zinc-50" src={workgridDataSource?.iconUrl} alt="" />
          <div className="min-w-0 flex-auto">
            <div className="text-left">
              <h2
                className={cn('text-base font-semibold text-zinc-900', {
                  'text-red-600': !isAuthenticated
                })}
              >
                {workgridDataSource?.name}
              </h2>
              {isAuthenticated && (
                <p className={cn('text-sm text-zinc-500', { 'text-red-600': !isAuthenticated })}>
                  {dataSourceV3?.name}
                </p>
              )}
              {!isAuthenticated && (
                <div className="flex">
                  <p className="text-balance text-xs text-red-600">{dataSourceV3?.name} needs re-authentication</p>
                </div>
              )}
            </div>
          </div>
          {!dataSourcesV3Query.isLoading && !isTemplate && (
            <div className="flex">
              <SharedWith
                sharedWith={dataSourceV3?.owners || []}
                isLoading={dataSourcesV3Query.isLoading}
                className="w-fit"
                isOverflowHidden={false}
              />
            </div>
          )}
          {!isAuthenticated && (
            <div className="flex items-center">
              <ExclamationCircleIcon className="h-6 w-6 stroke-red-600" />
            </div>
          )}
        </MenuButton>
        {(datasourceOrgAdminAccessFlagOn
          ? (hasOrgAdminRole && !dataSourcesV3Query.isLoading) || (!readOnly && !dataSourcesV3Query.isLoading)
          : !readOnly && !dataSourcesV3Query.isLoading) && (
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems
              className={cn(
                'absolute z-50 mt-2 w-48 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                'right-0 origin-top-right'
              )}
            >
              <div className="py-1">
                {(datasourceOrgAdminAccessFlagOn ? isDataSourceOwner || hasOrgAdminRole : isDataSourceOwner) && (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={cn(
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'group flex w-full items-center px-4 py-2 text-sm'
                        )}
                        onClick={() => dataSourceV3 && onEdit?.(dataSourceV3)}
                      >
                        <PencilSquareIcon
                          className="mr-3 h-5 w-5 text-zinc-400 group-hover:text-zinc-500"
                          aria-hidden="true"
                        />
                        {isDataSourceOwner ? 'Configure' : 'Open'}
                      </button>
                    )}
                  </MenuItem>
                )}
                {!readOnly && !dataSourcesV3Query.isLoading && (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={cn(
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'group flex w-full items-center px-4 py-2 text-sm'
                        )}
                        onClick={() => dataSourceV3 && onSwapInstance?.(dataSourceV3)}
                      >
                        <ArrowPathRoundedSquareIcon
                          className="mr-3 h-5 w-5 rotate-90 text-zinc-400 group-hover:text-zinc-500"
                          aria-hidden="true"
                        />
                        Swap Instance
                      </button>
                    )}
                  </MenuItem>
                )}

                {!readOnly && (
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={cn(
                          focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                          'group flex w-full items-center px-4 py-2 text-sm'
                        )}
                        onClick={() => dataSourceV3 && onDelete?.(dataSourceV3)}
                      >
                        <TrashIcon
                          className="mr-3 h-5 w-5 text-zinc-400 group-hover:text-zinc-500"
                          aria-hidden="true"
                        />
                        Remove
                      </button>
                    )}
                  </MenuItem>
                )}
              </div>
            </MenuItems>
          </Transition>
        )}
      </Menu>

      <div className="w-1/6 items-center whitespace-nowrap pr-4 text-right text-sm font-medium sm:pr-3">
        {isDeleting && (
          <div className="flex items-center justify-center">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </li>
  )
}
